import React, { useEffect, useState } from "react";
import { useThemeStore } from "@shared/stores";
import HStack from "@components/layout/HStack";
import Button from "./Button";
import { CloseIcon } from "../icons/IconCustom";

type Props = {
  title?: string;
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  iconRight?: React.ReactNode;
  onRenderHeader?: () => React.ReactNode;
  hideCloseButton?: boolean;
  width?: string;
};

const SlideUpPanel = ({ title, isOpen, onClose, children, iconRight, onRenderHeader, hideCloseButton, width }: Props) => {
  const [shouldMount, setShouldMount] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldMount(true);
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else if (shouldMount) {
      setIsAnimating(false);
      const timeout = setTimeout(() => {
        setShouldMount(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isOpen, shouldMount]);

  if (!shouldMount) return null;

  return (
    <SlideUpPanelContent
      isAnimating={isAnimating}
      onClose={onClose}
      title={title}
      iconRight={iconRight}
      onRenderHeader={onRenderHeader}
      hideCloseButton={hideCloseButton}
      children={children}
      width={width}
    />
  );
};

const SlideUpPanelContent = ({
  isAnimating,
  onClose,
  title,
  iconRight,
  onRenderHeader,
  hideCloseButton,
  children,
  width,
}: {
  isAnimating: boolean;
  onClose?: () => void; 
  title?: string;
  iconRight?: React.ReactNode;
  onRenderHeader?: () => React.ReactNode;
  hideCloseButton?: boolean;
  children: React.ReactNode;
  width?: string;
}) => {
  const { isDarkMode } = useThemeStore();

  const handleClose = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/30" style={{ width: width || undefined }} onClick={handleClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`${
          isAnimating ? "animate-slide-up" : "animate-slide-down"
        } flex flex-col w-full h-[95%] overflow-hidden bg-white rounded-t-xl shadow-elevationShadow4 transform translate-y-full dark:shadow-elevationShadow4Dark dark:bg-darkBackgroundColor`}
      >
        {/* Header */}
        {onRenderHeader ? (
          onRenderHeader()
        ) : (
          <HStack
            className="w-full px-5 py-4 border-b border-lightGray dark:border-darkMediumGray"
            justifyContent="between"
          >
            <div>
              {!hideCloseButton && (
                <Button leftIcon={<CloseIcon isDarkMode={isDarkMode} size={20} />} onClick={onClose} />
              )}
            </div>
            <p className="text-[21px] font-semibold max-w-[80%] truncate">{title}</p>
            <div className="w-6 h-6">{iconRight}</div>
          </HStack>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto no-scrollbar w-full">{children}</div>
      </div>
    </div>
  );
};

export default SlideUpPanel;
